import { useIntl } from "react-intl";
import { Link, Outlet, useLocation } from "react-router-dom";
import Logo from "../components/common/Logo";
import Tooltip from "@mui/material/Tooltip";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import styled from "@mui/material/styles/styled";
import Drawer from "@mui/material/Drawer";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import DirectionsRun from "@mui/icons-material/DirectionsRun";
import ShoppingCart from "@mui/icons-material/ShoppingCart";
import Help from "@mui/icons-material/Help";
import Person from "@mui/icons-material/Person";
import Menu from "@mui/icons-material/Menu";
import React from "react";
import { observer } from "mobx-react-lite";
import { useContext, useState } from "react";
import PermissionDialog from "../components/common/PermissionDialog";
import { AppContext } from "../contexts/AppContext";
import dayjs from "dayjs";
import { Permission } from "../services/UserService";
import DirectionsBike from "@mui/icons-material/DirectionsBike";
import posthog from "posthog-js";
import Lightbulb from "@mui/icons-material/Lightbulb";
import Search from "@mui/icons-material/Search";
import LocalPizza from "@mui/icons-material/LocalPizza";

const Main = styled('main')(({ theme }) => ({
  marginLeft: theme.spacing(8),
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    marginTop: theme.spacing(7),
  },
  "@media print": {
    margin: 0,
  }
}));

const DesktopNavbar = styled(Drawer)({
  "@media print": {
    display: "none"
  }
});

const NavIcon = styled(ListItemIcon)<{ selected?: boolean }>(({ theme, selected }) => ({
  minWidth: 0,
  justifyContent: 'center',
  color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
}));

export interface INavigationOption {
  name: string;
  href: string;
  icon: React.ElementType;
  routes: string[];
  index?: boolean;
  onClick?: (e: React.MouseEvent) => void;
};

const DesktopNavListItem: React.FC<INavigationOption> = (navigationOption) => {
  const location = useLocation();
  const currentPath = location?.pathname;

  return (
    <Tooltip title={navigationOption.name} placement="right" arrow>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          sx={{ minHeight: 48, justifyContent: 'center', px: 2.5 }}
          component={Link}
          to={navigationOption.href}
          onClick={navigationOption.onClick}
        >
          <NavIcon selected={(navigationOption.index && currentPath === "/") || navigationOption.routes.some(route => currentPath.includes(route))}>
            {navigationOption.icon && <navigationOption.icon />}
          </NavIcon>
        </ListItemButton>
      </ListItem>
    </Tooltip>
  );
};

const MobileNavListItem: React.FC<INavigationOption & { onNavigate: () => void }> = (props) => {
  const location = useLocation();
  const currentPath = location?.pathname;
  const isSelected = (props.index && currentPath === "/") || props.routes.some(route => currentPath.includes(route));

  return (
    <ListItem disablePadding>
      <ListItemButton
        component={Link}
        to={props.href}
        onClick={(e: React.MouseEvent) => {
          props.onClick?.(e);
          if (!e.defaultPrevented) {
            props.onNavigate();
          }
        }}
        selected={isSelected}
      >
        <ListItemIcon sx={{ color: isSelected ? 'primary.main' : 'text.secondary', minWidth: 40 }}>
          {props.icon && <props.icon />}
        </ListItemIcon>
        <ListItemText
          primary={props.name}
          primaryTypographyProps={{ fontWeight: isSelected ? 700 : 400 }}
          sx={{ color: isSelected ? 'primary.main' : 'text.primary' }}
        />
      </ListItemButton>
    </ListItem>
  );
};

const Root: React.FC = observer(() => {

  const intl = useIntl();

  const theme = useTheme();
  const isUpMD = useMediaQuery(theme.breakpoints.up('md'));

  const { userService } = useContext(AppContext);

  const permission = userService.isLoadingUser ?
    Permission.loading :
    userService.user?.permissions?.includes("admin") ? Permission.admin :
      (userService.user?.proExpDate && dayjs(userService.user.proExpDate).add(1, "day").isAfter(dayjs(), "day") &&
        userService.user.subscriptionStatus === "active"
      ) ? Permission.pro : Permission.free;

  const location = useLocation();
  const currentPath = location?.pathname;

  const [nutritionalTrainingDialogOpen, setNutritionalTrainingDialogOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  if (currentPath.includes("/_external")) {
    return <Outlet />;
  }

  const navigationOptions = {
    main: [
      { name: intl.formatMessage({ id: "nav.plans" }), href: '/plans', icon: userService.user?.preferedSport === "bike" ? DirectionsBike : DirectionsRun, routes: ['/plans'], index: true, onClick: () => posthog.capture('plans_clicked') },
      {
        name: intl.formatMessage({ id: "nav.races" }),
        href: '/races',
        icon: Search,
        routes: ['/races'],
        onClick: () => posthog.capture('races_clicked')
      },
      {
        name: intl.formatMessage({ id: "nav.nutritionalTraining" }),
        href: '/nutritional-training',
        icon: LocalPizza,
        routes: ['/nutritional-training'],
        onClick: (e: React.MouseEvent) => {
          posthog.capture('nutritional_training_clicked');
          if (permission === Permission.free) {
            e.preventDefault();
            setNutritionalTrainingDialogOpen(true);
          }
        }
      },
    ],
    secondary: [
      { name: intl.formatMessage({ id: "nav.featureRequests" }), href: '/feature-requests', icon: Lightbulb, routes: ['/feature-requests'], onClick: () => posthog.capture('feature-requests_clicked') },
      { name: intl.formatMessage({ id: "nav.help" }), href: '/help', icon: Help, routes: ['help'], onClick: () => posthog.capture('help_clicked') },
      { name: intl.formatMessage({ id: "nav.account" }), href: '/account', icon: Person, routes: ['/account', '/sign-in', 'sign-up', 'forgot-password'], onClick: () => posthog.capture('account_clicked') },
    ],
  };

  if (!userService.user) {
    navigationOptions.main = [];
    navigationOptions.secondary = navigationOptions.secondary.filter(option => option.href !== '/account');
  } else {
    if (permission === Permission.free) {
      navigationOptions.main.push(
        { name: intl.formatMessage({ id: "action.getPro" }), href: '/checkout', icon: ShoppingCart, routes: ['/checkout'], index: false, onClick: () => posthog.capture('get_pro_clicked') }
      );
    }
  }

  return (
    <Box>
      {/* Desktop: permanent icon-only sidebar */}
      {isUpMD && (
        <DesktopNavbar variant="permanent" anchor="left" sx={{ display: 'flex' }}
          PaperProps={{ sx: { flexDirection: "column", justifyContent: "space-between", alignItems: "center" } }}
        >
          <List>
            {navigationOptions.main.map((navigationOption) => (
              <DesktopNavListItem key={navigationOption.name} {...navigationOption} />
            ))}
          </List>
          <List sx={{ marginTop: 'auto' }}>
            {navigationOptions.secondary.map((navigationOption) => (
              <DesktopNavListItem key={navigationOption.name} {...navigationOption} />
            ))}
          </List>
        </DesktopNavbar>
      )}

      {/* Mobile: AppBar with hamburger + temporary drawer */}
      {!isUpMD && (
        <>
          <AppBar
            position="fixed"
            color="default"
            elevation={1}
            sx={{ zIndex: theme.zIndex.drawer + 1, "@media print": { display: "none" } }}
          >
            <Toolbar sx={{ justifyContent: "space-between" }}>
              <IconButton edge="start" onClick={() => setMobileDrawerOpen(prev => !prev)} aria-label="menu">
                <Menu />
              </IconButton>
              <Logo width={80} />
              <Box width={40} />
            </Toolbar>
          </AppBar>

          <Drawer
            anchor="left"
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            sx={{ "@media print": { display: "none" } }}
          >
            <Box sx={{ width: 260, pt: `${theme.mixins.toolbar.minHeight}px` }} role="presentation">
              <List>
                {navigationOptions.main.map((navigationOption) => (
                  <MobileNavListItem
                    key={navigationOption.name}
                    {...navigationOption}
                    onNavigate={() => setMobileDrawerOpen(false)}
                  />
                ))}
              </List>
              <Divider />
              <List>
                {navigationOptions.secondary.map((navigationOption) => (
                  <MobileNavListItem
                    key={navigationOption.name}
                    {...navigationOption}
                    onNavigate={() => setMobileDrawerOpen(false)}
                  />
                ))}
              </List>
            </Box>
          </Drawer>
        </>
      )}

      <Main>
        <Outlet />
      </Main>
      <PermissionDialog
        open={nutritionalTrainingDialogOpen}
        onClose={() => setNutritionalTrainingDialogOpen(false)}
        title={intl.formatMessage({ id: "permission.nutritionalTrainingTitle" })}
        text={intl.formatMessage({ id: "permission.nutritionalTrainingText" })}
        video="/videos/nutritionplanner.mp4"
      />
    </Box>
  );
});

export default Root;
